import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Filter } from "lucide-react";
import Seo from "@/components/Seo";
import PlanCard from "../components/PlanCard";
import PlanDisclosureBlock from "../components/PlanDisclosureBlock";
import CompensationDisclosure from "../components/CompensationDisclosure";
import CityMatchBanner from "../components/CityMatchBanner";
import useCurrentState from "@/routes/useCurrentState";
import { hasFeature, statePath } from "@/lib/stateRegistry";
import { PUBLIC_LAUNCH_STATE_CODE } from "@/lib/displayFlags";
import { fetchPlans, fetchProviders, fetchNearestCity } from "../lib/api";
import { formatLongDate } from "../lib/planCosts";
import { TDU_DISPLAY, TDU_CATALOG_KEY } from "@/lib/texasTduMap";

// TDU (utility area) reference list for the "Choose your utility area" chip. `TDU_NAMES` covers the
// older 4-entry catalog codes ("aep-texas" combining Central+North) for back-compat with any link
// already using that value directly; `TDU_DISPLAY` (imported above, from the Utilities map's
// 6-entry set) is checked first and covers the richer set including the split AEP zones and
// Lubbock -- see `TDU_CATALOG_KEY` below for how a map code becomes a catalog filter value.
const TDU_NAMES = { oncor: "Oncor Electric Delivery", centerpoint: "CenterPoint Energy", "aep-texas": "AEP Texas", tnmp: "Texas-New Mexico Power" };

const TERMS = [
  { label: "Any", min: 0, max: 999 },
  { label: "Month-to-month", min: 1, max: 1 },
  { label: "6-12 months", min: 6, max: 12 },
  { label: "12-24 months", min: 12, max: 24 },
  { label: "24+ months", min: 24, max: 999 },
];

export default function Plans() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  // State comes from the URL path (/{state}/plans), validated against STATE_REGISTRY by the router.
  const stateEntry = useCurrentState();
  const state = stateEntry.code;
  const usageTiers = hasFeature(stateEntry, "usage_tiers"); // Texas EFL 3-tier display only
  const energyTypes = (stateEntry.tracks.residential && stateEntry.tracks.residential.energy_types) || ["electricity"];
  const zip = searchParams.get("zip") || "";
  const energyType = searchParams.get("energy_type") || "";
  const planType = searchParams.get("plan_type") || "";
  const providerId = searchParams.get("provider_id") || "";
  const greenOnly = searchParams.get("green_only") === "true";
  const solarBuyback = searchParams.get("solar_buyback") === "true";
  const termIdx = parseInt(searchParams.get("term") || "0", 10);
  const sort = searchParams.get("sort") || "rate";
  const usageLevel = parseInt(searchParams.get("usage") || "1000", 10);
  const tdu = searchParams.get("tdu") || "";
  // Translate a Utilities-map 6-entry code (e.g. "aep_texas_central") to the 4-entry value actually
  // stored on plan documents (TDU_CATALOG_KEY, mirroring backend/billanalysis/tdu.py's catalog_tdu).
  // If `tdu` isn't one of the 6 map codes, pass it through as-is (back-compat with any link already
  // using a catalog value like "aep-texas" directly). `catalogTdu === null` is the honest "this zone
  // has no plans in the catalog today" case (Lubbock) -- distinct from "" (no filter at all).
  const hasMapCode = Object.prototype.hasOwnProperty.call(TDU_CATALOG_KEY, tdu);
  const catalogTdu = tdu ? (hasMapCode ? TDU_CATALOG_KEY[tdu] : tdu) : "";
  const tduHasNoPlans = tdu !== "" && hasMapCode && catalogTdu === null;

  // Texas-only city-skyline banner (see CityMatchBanner.jsx): which of the 6 major TX metros
  // this ZIP is nearest to, straight-line-approximated by GET /api/nearest-city. Starts at
  // null (neutral/no-photo fallback) rather than defaulting to any specific city, so a slow
  // response never briefly shows the wrong metro -- only a real, confirmed match ever swaps it.
  const [nearestCityKey, setNearestCityKey] = useState(null);
  useEffect(() => {
    if (state !== PUBLIC_LAUNCH_STATE_CODE || !/^\d{5}$/.test(zip)) {
      setNearestCityKey(null);
      return;
    }
    let cancelled = false;
    fetchNearestCity(zip)
      .then((res) => { if (!cancelled) setNearestCityKey(res.city_key || null); })
      .catch(() => { if (!cancelled) setNearestCityKey(null); }); // network/lookup failure -> honest neutral fallback, never a guess
    return () => { cancelled = true; };
  }, [state, zip]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(searchParams);
    if (v === "" || v === false || v === null || v === undefined) next.delete(k);
    else next.set(k, String(v));
    setSearchParams(next);
  };

  useEffect(() => {
    fetchProviders({ state }).then(setProviders).catch(() => {});
  }, [state]);

  useEffect(() => {
    // Honest empty state for a zone the catalog has no plans tagged for (e.g. Lubbock Power &
    // Light) -- skip the network call entirely rather than either querying with a nonsense value
    // or silently dropping the filter and showing every plan in the state.
    if (tduHasNoPlans) {
      setPlans([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = TERMS[termIdx] || TERMS[0];
    const params = {};
    params.state = state;
    if (energyType) params.energy_type = energyType;
    if (planType) params.plan_type = planType;
    if (providerId) params.provider_id = providerId;
    if (catalogTdu) params.tdu = catalogTdu;
    if (greenOnly) params.green_only = true;
    if (solarBuyback) params.solar_buyback = true;
    if (t.min) params.min_term = t.min;
    if (t.max < 999) params.max_term = t.max;
    params.sort = sort;
    fetchPlans(params)
      .then((d) => setPlans(d))
      .finally(() => setLoading(false));
  }, [state, energyType, planType, providerId, catalogTdu, tduHasNoPlans, greenOnly, solarBuyback, termIdx, sort]);

  const providerMap = useMemo(() => {
    const m = {};
    providers.forEach((p) => (m[p.id] = p));
    return m;
  }, [providers]);

  // Client-side re-sort based on selected usage level when sorting by estimated cost, and for "provider" (the
  // backend has no provider-name sort field to query against, Design Specification Section 8.4).
  const displayedPlans = useMemo(() => {
    if (!plans.length) return plans;
    const key = usageLevel === 500 ? "rate_500" : usageLevel === 2000 ? "rate_2000" : "rate_1000";
    if (sort === "rate") return [...plans].sort((a, b) => a[key] - b[key]);
    if (sort === "provider") {
      return [...plans].sort((a, b) => {
        const an = (providerMap[a.provider_id] && providerMap[a.provider_id].name) || "";
        const bn = (providerMap[b.provider_id] && providerMap[b.provider_id].name) || "";
        return an.localeCompare(bn);
      });
    }
    return plans;
  }, [plans, sort, usageLevel, providerMap]);

  const verifiedOn = useMemo(() => {
    if (!plans.length) return null;
    const latest = plans.reduce((max, p) => (p.verified_at && (!max || p.verified_at > max) ? p.verified_at : max), null);
    return formatLongDate(latest);
  }, [plans]);

  const activeFilterCount =
    (energyType ? 1 : 0) + (planType ? 1 : 0) + (providerId ? 1 : 0) + (greenOnly ? 1 : 0) + (solarBuyback ? 1 : 0) + (termIdx ? 1 : 0);

  const clearAll = () => {
    const next = new URLSearchParams();
    if (zip) next.set("zip", zip);
    setSearchParams(next);
  };

  return (
    <div data-testid="plans-page" data-state={stateEntry.slug}>
      <Seo
        title={`${stateEntry.name} energy plans`}
        description={`Compare energy plans in ${stateEntry.name} by estimated monthly cost.`}
        path={statePath(stateEntry.slug, "plans")}
        noindex
      />
      <section className="bg-[#F7F9FC] border-b border-zinc-100">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-12">
          {state === PUBLIC_LAUNCH_STATE_CODE && <CityMatchBanner cityKey={nearestCityKey} />}
          <div className={`flex flex-wrap items-center gap-2 mb-4 ${state === PUBLIC_LAUNCH_STATE_CODE ? "mt-6" : ""}`}>
            <span className="pill">Available plans</span>
            {verifiedOn && <span className="pill" data-testid="plans-verified-chip">Rates verified as of {verifiedOn}</span>}
            {tdu && (
              <span className="pill" data-testid="plans-tdu-chip">
                Utility area: {TDU_DISPLAY[tdu]?.name || TDU_NAMES[tdu] || tdu}{" "}
                <Link to={statePath(stateEntry.slug)} className="underline ml-1">(change)</Link>
              </span>
            )}
          </div>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <h1 className="font-display text-4xl md:text-5xl tracking-tight font-extrabold">
              {`Plans in ${stateEntry.name}`}
            </h1>
            <div className="flex items-center gap-3">
              <span className="text-sm text-zinc-500" role="status" aria-live="polite">
                {loading ? "…" : `${plans.length} plans, sorted by estimated monthly cost${usageTiers ? ` at ${usageLevel} kWh` : ""}`}
              </span>
              <select
                value={sort}
                onChange={(e) => setParam("sort", e.target.value)}
                className="input-brutal w-auto text-sm"
                aria-label="Sort plans by"
                data-testid="sort-select"
              >
                <option value="rate">Sort: Estimated monthly cost (low → high)</option>
                <option value="term">Sort: Shortest term</option>
                <option value="provider">Sort: Provider name (A → Z)</option>
              </select>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="btn-outline lg:hidden"
                aria-expanded={showFilters}
                aria-controls="filters-sidebar"
                data-testid="filters-toggle"
              >
                <Filter className="w-4 h-4" aria-hidden="true" /> Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
              </button>
            </div>
          </div>

          {/* Usage-level selector — PUCT Electricity Facts Label tiers (Texas only). A real radio group
              (Design Specification F9): the previous role="tablist" markup offered no tab keyboard behavior. */}
          {usageTiers && (
            <fieldset className="mt-7 bg-white border border-slate-200 rounded-2xl p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4 md:gap-6" data-testid="usage-selector">
              <div className="flex-1">
                <legend className="font-semibold text-sm text-[#0A2540]">Monthly electricity use</legend>
                <p className="text-xs text-slate-500 mt-0.5">Not sure? Your bill shows your monthly kWh.</p>
              </div>
              <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1 shrink-0" role="radiogroup" aria-label="Monthly electricity use">
                {[500, 1000, 2000].map((v) => (
                  <label
                    key={v}
                    className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer min-h-[44px] flex items-center ${
                      usageLevel === v ? "bg-[#16A34A] text-white shadow-md" : "text-slate-700 hover:bg-white"
                    }`}
                  >
                    <input
                      type="radio"
                      name="usage-level"
                      className="sr-only"
                      checked={usageLevel === v}
                      onChange={() => setParam("usage", v)}
                      data-testid={`usage-${v}`}
                    />
                    {v.toLocaleString()} kWh
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          <PlanDisclosureBlock effectiveDate={verifiedOn} className="mt-6" testId="plans-disclosure-top" />
        </div>
      </section>

      <section>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 grid grid-cols-12 gap-8">
          {/* Filters */}
          <aside id="filters-sidebar" className={`col-span-12 lg:col-span-3 ${showFilters ? "block" : "hidden lg:block"}`} aria-label="Filter plans" data-testid="filters-sidebar">
            <div className="bg-white border border-zinc-100 rounded-2xl p-6 sticky top-20 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <p className="font-display text-lg font-bold">Filters</p>
                {activeFilterCount > 0 && (
                  <button onClick={clearAll} className="text-xs text-zinc-500 hover:text-black underline" data-testid="clear-filters">
                    Clear all
                  </button>
                )}
              </div>

              {energyTypes.length > 1 && (
                <FilterGroup label="Energy">
                  <div className="flex flex-col gap-2">
                    {[
                      { v: "", l: "All" },
                      { v: "electricity", l: "Electricity" },
                      { v: "gas", l: "Natural gas" },
                    ].map((o) => (
                      <label key={o.v} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`filter-energy-${o.v || "all"}`}>
                        <input type="radio" name="energy" checked={energyType === o.v} onChange={() => setParam("energy_type", o.v)} className="accent-[#16A34A]" />
                        {o.l}
                      </label>
                    ))}
                  </div>
                </FilterGroup>
              )}

              {/* Indexed excluded for Texas residential/small commercial (Design Specification F2; 16 TAC
                  25.475(c)). This filter row lists only the plan types that can lawfully be shown here. */}
              <FilterGroup label="Plan type">
                <div className="flex flex-col gap-2">
                  {[
                    { v: "", l: "All" },
                    { v: "fixed", l: "Fixed" },
                    { v: "variable", l: "Variable" },
                  ].map((o) => (
                    <label key={o.v} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`filter-type-${o.v || "all"}`}>
                      <input type="radio" name="plan_type" checked={planType === o.v} onChange={() => setParam("plan_type", o.v)} className="accent-[#16A34A]" />
                      {o.l}
                    </label>
                  ))}
                </div>
              </FilterGroup>

              <FilterGroup label="Term length">
                <div className="flex flex-col gap-2">
                  {TERMS.map((t, i) => (
                    <label key={i} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`filter-term-${i}`}>
                      <input type="radio" checked={termIdx === i} onChange={() => setParam("term", i || "")} className="accent-[#16A34A]" />
                      {t.label}
                    </label>
                  ))}
                </div>
              </FilterGroup>

              <FilterGroup label="Provider" as="label" htmlFor="filter-provider-select">
                <select id="filter-provider-select" value={providerId} onChange={(e) => setParam("provider_id", e.target.value)} className="input-brutal" data-testid="filter-provider">
                  <option value="">All providers</option>
                  {providers.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </FilterGroup>

              <label className="flex items-center gap-3 mt-2 cursor-pointer" data-testid="filter-green-only">
                <input type="checkbox" checked={greenOnly} onChange={(e) => setParam("green_only", e.target.checked)} className="w-4 h-4 accent-[#16A34A]" />
                <span className="text-sm font-semibold">100% renewable only</span>
              </label>

              <label className="flex items-center gap-3 mt-3 cursor-pointer" data-testid="filter-solar-buyback">
                <input type="checkbox" checked={solarBuyback} onChange={(e) => setParam("solar_buyback", e.target.checked)} className="w-4 h-4 accent-[#16A34A]" />
                <span className="text-sm font-semibold">Solar buyback only</span>
              </label>
            </div>
          </aside>

          {/* Results */}
          <div className="col-span-12 lg:col-span-9 space-y-4">
            {loading && <div className="text-slate-500 py-12">Loading plans…</div>}
            {!loading && displayedPlans.length === 0 && (
              <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-16 text-center" data-testid="no-plans">
                <p className="font-display text-2xl font-bold mb-2 text-[#0A2540]">No plans match these filters.</p>
                <p className="text-slate-500 text-sm mb-6">Try widening your search or clearing filters.</p>
                <button onClick={clearAll} className="btn-green">Clear filters</button>
              </div>
            )}
            {!loading && displayedPlans.map((plan) => (
              <PlanCard key={plan.id} plan={plan} provider={providerMap[plan.provider_id]} usageLevel={usageLevel} stateSlug={stateEntry.slug} usageTiers={usageTiers} />
            ))}

            {!loading && displayedPlans.length > 0 && (
              <div className="pt-6 space-y-5">
                <PlanDisclosureBlock effectiveDate={verifiedOn} testId="plans-disclosure-bottom" />
                <CompensationDisclosure testId="plans-compensation-disclosure" />
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

/**
 * Groups a set of related filter controls with the real ARIA pattern for each shape:
 * radio-button groups (Energy/Plan type/Term length) get <fieldset>/<legend> so a screen
 * reader announces the group name once, not per-option; a single control (Provider) gets
 * a real <label htmlFor> pointing at that control's id instead of a floating, unassociated
 * heading-like <p>.
 */
function FilterGroup({ label, children, as = "fieldset", htmlFor }) {
  const wrapperClass = "mb-5 pb-5 border-b border-zinc-100 last:border-b-0 last:pb-0 last:mb-0";
  if (as === "label") {
    return (
      <div className={wrapperClass}>
        <label htmlFor={htmlFor} className="text-overline text-zinc-500 mb-3 block">{label}</label>
        {children}
      </div>
    );
  }
  return (
    <fieldset className={wrapperClass}>
      <legend className="text-overline text-zinc-500 mb-3">{label}</legend>
      {children}
    </fieldset>
  );
}
